import Box from '@mui/material/Box';
import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

import ContentCardGrid from './ContentCardGrid';
import FilterTags from './FilterTags';
import NavBar from './NavBar';
import Project from './Project';
import {
  handleFilterTags,
  handleActiveProject,
} from './helpers/navigationHelper';

const Dashboard = ({ content }: { content: Content }) => {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { projectTitle } = useParams();
  const navigate = useNavigate();

  const { dashboard, categories, projects, cards } = content;
  const { title, description } = dashboard;

  const filterTags =
    searchParameters
      .get('filter')
      ?.split('_')
      .map((tag) => tag.replaceAll('-', ' ')) ?? [];
  const activeProject = projectTitle?.replaceAll('-', ' ') ?? '';
  const allTags =
    categories.find(
      (category) => category.name.toLocaleLowerCase() === 'alltags',
    )?.tags[0].name ?? '';

  const [selectedIcon, setSelectedIcon] = useState(0);
  const [searchedProjects, setSearchedProjects] = useState([] as Project[]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSelectedTags = (inputTagName: string) => {
    const tagName = inputTagName.toLocaleLowerCase().replaceAll('-', ' ');
    if (tagName === allTags.toLocaleLowerCase()) {
      handleFilterTags([], setSearchParameters);
    } else if (filterTags.includes(tagName)) {
      const filteredTags = filterTags.filter((tag: string) => tag !== tagName);
      handleFilterTags(filteredTags, setSearchParameters);
    } else {
      handleFilterTags([...filterTags, tagName], setSearchParameters);
    }
  };

  const project = projects.find(
    (project) => project.title.toLocaleLowerCase() === activeProject,
  );

  const sortedProjects = isSearching
    ? searchedProjects.toSorted((a, b) => a.title.localeCompare(b.title))
    : projects.toSorted((a, b) => a.title.localeCompare(b.title));

  const filteredProjects = isSearching
    ? searchedProjects
        .filter((project) => {
          const tags = new Set(
            project.tags.map((tag) => tag.toLocaleLowerCase()),
          );
          return filterTags.some((tag) => tags.has(tag));
        })
        .sort((a, b) => a.title.localeCompare(b.title))
    : projects
        .filter((project) => {
          const tags = new Set(
            project.tags.map((tag) => tag.toLocaleLowerCase()),
          );
          return filterTags.some((tag) => tags.has(tag));
        })
        .sort((a, b) => a.title.localeCompare(b.title));

  const showAllTags = filterTags.length === 0;

  return (
    <div className="dashboard">
      <NavBar
        isFeatured={false}
        projects={projects}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        setSearchedProjects={setSearchedProjects}
      />
      <div className="dashboard-wrapper">
        <FilterTags
          categories={categories}
          selectedTags={filterTags}
          handleSelectedTags={handleSelectedTags}
          allTags={allTags}
          selectedIcon={selectedIcon}
          setSelectedIcon={setSelectedIcon}
          navigate={navigate}
        />
        <Box
          className="dashboard-content"
          component="main"
          sx={{ flexGrow: 1, p: 3 }}
        >
          <div className="intro">
            <span className="title">{title}</span>
            <span className="description">{description}</span>
          </div>
          <ContentCardGrid
            projects={showAllTags ? sortedProjects : filteredProjects}
            handleActiveProject={handleActiveProject}
            cardsAction={cards.action}
            isFeatured={false}
            navigate={navigate}
            searchParameters={searchParameters}
          />
          {project && (
            <Project
              project={project}
              handleActiveProject={handleActiveProject}
              navigate={navigate}
              searchParameters={searchParameters}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default Dashboard;
